import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Clock, Loader2, Check, LogOut } from 'lucide-react';
import { ClockInButton, ClockOutButton } from './ClockButtons';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function QuickClockModal({ open, onOpenChange }) {
  const [locationCode, setLocationCode] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setResult(null);
      setPin('');
      setLocationCode('');
      setError('');
    }
  }, [open]);

  const handleLocationCodeChange = (e) => {
    setLocationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
    if (error) setError('');
  };

  const handlePinChange = (e) => {
    setPin(e.target.value.replace(/\D/g, '').slice(0, 4));
    if (error) setError('');
  };

  const canSubmit = locationCode.length === 6 && pin.length === 4 && !loading;

  const handleClock = async (action) => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const endpoint = action === 'in' ? '/attendance/clock-in' : '/attendance/clock-out';
      const { data } = await axios.post(`${API_BASE}${endpoint}`, {
        location_code: locationCode,
        staff_pin: pin,
      });
      setResult({ ...data, action });
      setTimeout(() => {
        setResult(null);
        setLocationCode('');
        setPin('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="text-orange-500" size={24} />
            Quick Clock
          </DialogTitle>
          <DialogDescription>
            Enter your location code and PIN to clock in or out
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-8 text-center">
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              result.action === 'in' ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              {result.action === 'in' ? (
                <Check className="text-green-600" size={32} />
              ) : (
                <LogOut className="text-blue-600" size={32} />
              )}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2" data-testid="clock-success-heading">
              {result.action === 'in' ? 'Clocked In!' : 'Clocked Out!'}
            </h3>
            <p className="text-slate-600" data-testid="clock-success-name">
              {result.user_name}
            </p>
            <p className="text-sm text-slate-500 mt-2" data-testid="clock-success-time">
              {(() => {
                const raw = result.action === 'in' ? result.clock_in_time : result.clock_out_time;
                const t = new Date(raw);
                return !isNaN(t) ? t.toLocaleTimeString() : raw;
              })()}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location-code">Location Code</Label>
              <Input
                id="location-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="6-digit location code"
                value={locationCode}
                onChange={handleLocationCodeChange}
                autoFocus
                data-testid="quick-clock-location-code-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pin">Your PIN</Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder="4-digit PIN"
                value={pin}
                onChange={handlePinChange}
                className="text-center text-2xl tracking-widest"
                data-testid="quick-clock-pin-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 text-center" data-testid="quick-clock-error">
                {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <ClockInButton
                loading={loading}
                disabled={!canSubmit}
                onClick={() => handleClock('in')}
              />
              <ClockOutButton
                loading={loading}
                disabled={!canSubmit}
                onClick={() => handleClock('out')}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default QuickClockModal;
